import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, Clock, Settings, User, CheckCircle, 
  ChevronRight, AlertCircle, CalendarDays, Filter, 
  Plus, MoreHorizontal, Layers, GitBranch, ArrowRight,
  UserCheck
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Separator } from '../ui/separator';

const IntelligentSchedulingSlide = () => {
    // Animation state for "Routing"
    const [routingStep, setRoutingStep] = useState(0);

    // Simulated "Routing" animation loop
    useEffect(() => {
        const interval = setInterval(() => {
            setRoutingStep((prev) => (prev + 1) % 4); 
            // 0: Idle/Start, 1: Checking Rules, 2: Filtering Ops, 3: Found Slot
        }, 3000); // 3 seconds per step
        return () => clearInterval(interval);
    }, []);

  return (
    <div className="h-full w-full bg-slate-50 flex items-center justify-center p-12 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
            <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-blue-100 rounded-full blur-[100px] opacity-60" />
            <div className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] bg-purple-100 rounded-full blur-[100px] opacity-60" />
            <div className="absolute top-[40%] left-[20%] w-[300px] h-[300px] bg-indigo-100 rounded-full blur-[80px] opacity-40" />
        </div>

        {/* Floating Agent Header (Top Left) */}
        <div className="absolute top-8 left-8 lg:left-12 z-50 flex items-center gap-4 bg-white/40 backdrop-blur-md p-2 pr-6 rounded-full border border-white/20 shadow-lg">
            <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-white shadow-sm">
                 <img src="/assets/amy.png" alt="Amy" className="w-full h-full object-cover" />
            </div>
            <div className="text-left">
                <h3 className="text-lg font-bold text-slate-900 leading-none">Amy</h3>
                <p className="text-xs text-blue-600 font-bold uppercase tracking-wide">Intelligent Scheduling</p>
            </div>
        </div>

        <div className="flex flex-row h-full w-full max-w-[1600px] gap-8 relative z-10 pt-0">
            {/* Left Side: Context */}
            <div className="w-[40%] flex flex-col justify-center space-y-8">
                
                <div className="space-y-4">
                    <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
                        The only AI that understands <span className="text-blue-600">complex time-block based operatory scheduling rules</span>.
                    </h2>
                    
                    <p className="text-lg text-slate-600 leading-relaxed">
                        While other AI agents can only take a message or book patients into one column, Asha's Intelligent Scheduling Rules Engine understands your nuanced scheduling rules, time blocks, and provider preferences. All of Asha's front office AI agents are able to leverage these rules to book perfectly.
                    </p>
                </div>

                <div className="flex items-center gap-4 text-sm font-medium text-slate-500">
                    <div className="flex items-center gap-2">
                        <CalendarDays className="w-4 h-4 text-blue-600" />
                        <span>Perfect PMS Sync</span>
                    </div>
                    <div className="w-1 h-1 rounded-full bg-slate-300" />
                    <div className="flex items-center gap-2">
                        <Layers className="w-4 h-4 text-purple-600" />
                        <span>Multi-Provider Logic</span>
                    </div>
                </div>
            </div>

            {/* Right Side: Visual Builder Mockup with Routing Animation */}
            <div className="w-[50%] h-full flex items-center justify-center relative">
                
                {/* Routing Overlay Animation - New Feature */}
                <AnimatePresence>
                    {routingStep > 0 && (
                        <motion.div 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="absolute z-20 top-4 right-4 bg-slate-900/90 backdrop-blur-md rounded-xl p-4 text-white w-[280px] shadow-2xl border border-white/10"
                        >
                            <div className="flex items-center justify-between mb-3 border-b border-white/10 pb-2">
                                <span className="text-xs font-bold text-blue-400 uppercase tracking-wider">AI Routing Logic</span>
                                <div className="flex gap-1">
                                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                                </div>
                            </div>
                            
                            <div className="space-y-3">
                                {/* Step 1: Request */}
                                <div className={`flex items-center gap-3 transition-opacity duration-500 ${routingStep >= 1 ? 'opacity-100' : 'opacity-30'}`}>
                                    <div className="p-1.5 bg-blue-500/20 rounded-lg text-blue-300">
                                        <UserCheck className="w-4 h-4" />
                                    </div>
                                    <div className="text-xs">
                                        <div className="font-medium text-slate-200">Patient Request</div>
                                        <div className="text-slate-400">Crown (D2740) • Tuesday</div>
                                    </div>
                                </div>

                                {/* Connector */}
                                <div className={`h-4 w-0.5 bg-slate-700 ml-3.5 transition-colors duration-500 ${routingStep >= 2 ? 'bg-blue-500' : ''}`} />

                                {/* Step 2: Rules Check */}
                                <div className={`flex items-center gap-3 transition-opacity duration-500 ${routingStep >= 2 ? 'opacity-100' : 'opacity-30'}`}>
                                    <div className="p-1.5 bg-purple-500/20 rounded-lg text-purple-300">
                                        <Settings className="w-4 h-4" />
                                    </div>
                                    <div className="text-xs">
                                        <div className="font-medium text-slate-200">Checking Rules...</div>
                                        <div className="text-slate-400">Matches "Dr. Smith - Tuesday"</div>
                                    </div>
                                </div>

                                {/* Connector */}
                                <div className={`h-4 w-0.5 bg-slate-700 ml-3.5 transition-colors duration-500 ${routingStep >= 3 ? 'bg-green-500' : ''}`} />

                                {/* Step 3: Result */}
                                <div className={`flex items-center gap-3 transition-opacity duration-500 ${routingStep >= 3 ? 'opacity-100' : 'opacity-30'}`}>
                                    <div className="p-1.5 bg-green-500/20 rounded-lg text-green-300">
                                        <Calendar className="w-4 h-4" />
                                    </div>
                                    <div className="text-xs">
                                        <div className="font-medium text-green-400">Slot Found</div>
                                        <div className="text-slate-400">Op 2 • 2:00 PM (90m)</div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <motion.div 
                    initial={{ y: 40, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.7, ease: "easeOut" }}
                    className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col h-[600px] relative z-10"
                >
                    {/* Header */}
                    <div className="h-16 border-b border-slate-100 flex items-center justify-between px-6 bg-slate-50/50">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                                <Settings className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="font-bold text-slate-900">Scheduling Rules Engine</h3>
                                <p className="text-xs text-slate-500">Practice Settings / Rules / New Rule</p>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <Button variant="outline" size="sm">Cancel</Button>
                            <Button size="sm" className="bg-blue-600 hover:bg-blue-700">Save Rule</Button>
                        </div>
                    </div>

                    {/* Builder Area */}
                    <div className="flex-1 p-6 bg-slate-50/30 overflow-hidden flex flex-col">
                        
                        {/* Rule Name - Compact */}
                        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm mb-4 flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-3 flex-1">
                                <label className="text-xs font-bold text-slate-500 uppercase">Rule Name:</label>
                                <span className="font-semibold text-slate-900 text-sm">Crown (D2740) Complex Routing</span>
                            </div>
                            <Badge className="bg-green-100 text-green-700 hover:bg-green-100 border-green-200">Active</Badge>
                        </div>

                        {/* Logic Blocks - Compact & Stacked */}
                        <div className="flex-1 flex flex-col gap-2 relative min-h-0">
                            {/* Vertical Line Connector */}
                            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-slate-200 -z-10" />
                            
                            {/* IF Block */}
                            <div className={`bg-white rounded-xl border transition-colors duration-500 shadow-sm flex flex-col flex-1 ${routingStep === 2 ? 'border-blue-400 ring-2 ring-blue-100' : 'border-blue-200'}`}>
                                <div className="bg-blue-50/50 px-4 py-2 border-b border-blue-100 flex items-center gap-2 flex-shrink-0">
                                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">IF</div>
                                    <span className="font-semibold text-blue-900 text-sm">Conditions are met</span>
                                </div>
                                
                                <div className="p-4 space-y-2 overflow-y-auto flex-1">
                                    {/* Condition 1 */}
                                    <div className="flex items-center gap-3 p-2 rounded-lg bg-slate-50 border border-slate-100">
                                        <Badge variant="outline" className="bg-white text-slate-500 font-mono text-[10px]">INSURANCE</Badge>
                                        <span className="text-xs text-slate-600">is</span>
                                        <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs">Delta Dental</Badge>
                                        <span className="text-xs text-slate-400 font-medium ml-auto">AND</span>
                                    </div>

                                    {/* Condition 2 */}
                                    <div className="flex items-center gap-3 p-2 rounded-lg bg-slate-50 border border-slate-100">
                                        <Badge variant="outline" className="bg-white text-slate-500 font-mono text-[10px]">PROCEDURE</Badge>
                                        <span className="text-xs text-slate-600">is</span>
                                        <Badge className="bg-purple-100 text-purple-700 border-purple-200 text-xs">Crown (D2740)</Badge>
                                        <span className="text-xs text-slate-400 font-medium ml-auto">AND</span>
                                    </div>

                                    <Button variant="ghost" size="sm" className="h-6 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 pl-1 w-fit">
                                        <Plus className="w-3 h-3 mr-1" /> Add Condition
                                    </Button>
                                </div>
                            </div>

                            {/* Arrow */}
                            <div className="flex justify-center -my-3 relative z-10 flex-shrink-0">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center border-4 border-slate-50 transition-colors duration-500 ${routingStep >= 2 ? 'bg-blue-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
                                    <ArrowRight className="w-4 h-4" />
                                </div>
                            </div>

                            {/* THEN Block */}
                            <div className={`bg-white rounded-xl border transition-colors duration-500 shadow-sm flex flex-col flex-1 mt-1 ${routingStep === 3 ? 'border-green-400 ring-2 ring-green-100' : 'border-green-200'}`}>
                                <div className="bg-green-50/50 px-4 py-2 border-b border-green-100 flex items-center gap-2 flex-shrink-0">
                                    <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-bold">THEN</div>
                                    <span className="font-semibold text-green-900 text-sm">Apply scheduling logic</span>
                                </div>
                                
                                <div className="p-4 space-y-2 overflow-y-auto flex-1">
                                    {/* Action 1 */}
                                    <div className="flex items-center gap-3">
                                        <div className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                                        <div className="flex-1 flex items-center gap-2 text-xs text-slate-700">
                                            <span>Schedule with</span>
                                            <Badge variant="outline" className={`bg-white font-mono font-medium border-slate-300 transition-colors duration-500 text-xs ${routingStep === 3 ? 'bg-green-50 border-green-300 text-green-700' : ''}`}>Dr. Sarah Smith</Badge>
                                        </div>
                                    </div>

                                    {/* Action 2 */}
                                    <div className="flex items-center gap-3">
                                        <div className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                                        <div className="flex-1 flex items-center gap-2 text-xs text-slate-700">
                                            <span>Restrict to time block</span>
                                            <Badge variant="outline" className={`bg-white font-mono font-medium border-slate-300 transition-colors duration-500 text-xs ${routingStep === 3 ? 'bg-green-50 border-green-300 text-green-700' : ''}`}>2:00 PM - 4:00 PM</Badge>
                                        </div>
                                    </div>

                                    {/* Action 3 */}
                                    <div className="flex items-center gap-3">
                                        <div className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                                        <div className="flex-1 flex items-center gap-2 text-xs text-slate-700">
                                            <span>Frequency</span>
                                            <Badge variant="outline" className={`bg-white font-mono font-medium border-slate-300 transition-colors duration-500 text-xs ${routingStep === 3 ? 'bg-green-50 border-green-300 text-green-700' : ''}`}>Every Other Wednesday</Badge>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    </div>
  );
};

export default IntelligentSchedulingSlide;

